import { useState, useRef, useEffect, memo, useCallback } from 'react'

import './TransposeControl.css'

// Types
import type { GuitarArrangement, Note } from '../presets/presetSchema'

// Utilities
import { NOTES } from '../utils/constants'
import { getCurrentKeyCenter, getTransposeDisplayText } from '../utils/transpose'

/**
 * TransposeControl component
 * Provides a dropdown interface for transposing all guitar arrangements
 * to different keys while maintaining their relative relationships
 */

interface TransposeControlProps {
  guitars: GuitarArrangement[];
  onTranspose: (targetKey: Note) => void;
}

export function TransposeControl({ guitars, onTranspose }: TransposeControlProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const currentKey = getCurrentKeyCenter(guitars);
  
  // Close dropdown when clicking outside
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowDropdown(false);
    }
  }, []);

  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showDropdown, handleClickOutside, handleEscapeKey]);

  const handleTranspose = useCallback((targetKey: Note) => {
    onTranspose(targetKey);
    setShowDropdown(false);
  }, [onTranspose]);

  // Don't render if no guitars
  if (!guitars || guitars.length === 0 || !currentKey) {
    return null;
  }

  return (
    <div className="transpose-control" ref={dropdownRef}>
      <button
        type="button"
        className={`transpose-button ${showDropdown ? 'active' : ''}`}
        onClick={() => setShowDropdown(!showDropdown)}
        title={`Transpose all guitars from ${currentKey}`}
      >
        <span className="music-note-icon">♪</span>
        {currentKey}
      </button>
      
      {showDropdown && (
        <div className="transpose-dropdown">
          <div className="transpose-grid">
            {NOTES.map((note) => {
              const isCurrentKey = note === currentKey;
              const displayText = isCurrentKey 
                ? `${note} (current)` 
                : getTransposeDisplayText(currentKey, note);
              
              return (
                <button
                  key={note}
                  type="button"
                  className={`transpose-option ${isCurrentKey ? 'current' : ''}`}
                  onClick={() => !isCurrentKey && handleTranspose(note)}
                  disabled={isCurrentKey}
                  title={displayText}
                >
                  <span className="note-name">{note}</span>
                  {isCurrentKey && <span className="current-indicator">●</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Memoize TransposeControl for performance optimization
export default memo(TransposeControl);
